import { type FC, useCallback } from "react";
import { Heading, LegacyTagKind, LegacyTagSize, LegacyTagVariant, Tag, Text } from "@stubhub/broadway-web";
import {
  BarcodeScanIcon,
  ChevronRightIcon,
  DollarCircleOutlineIcon,
  FanProtectFillIcon,
  HistoryIcon,
} from "@stubhub/broadway-web/icons/functional";
import { Content, FormatContent } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useIsStubhub } from "src/shared/utils/appContextHelper";
import {
  FAN_PROTECT_TRADEMARK,
  VIAGOGO_GUARANTEE_CONTENT_ID,
} from "src/website-apps/shared/components/brand-guarantee/constants";
import { contentIds } from "./contentIds";
import { bannerCardRecipe, iconTile } from "./styles";

export const BannerCard: FC = () => {
  const storePageEvent = useStorePageEvent();
  const isStubhub = useIsStubhub();
  const styles = bannerCardRecipe();

  const chips = [
    {
      icon: HistoryIcon,
      contentId: contentIds.chipCardOnTime,
      value: "on_time",
    },
    {
      icon: BarcodeScanIcon,
      contentId: contentIds.chipCardValid,
      value: "valid",
    },
    {
      icon: DollarCircleOutlineIcon,
      contentId: contentIds.chipProtected,
      value: "protected",
    },
  ];

  const handleChipClick = useCallback(
    (chipValue: string) => {
      storePageEvent(PE_TYPE.CLICK, PE_KEYS.FAN_PROTECT_MODULE_CHIP_CLICK, chipValue);
    },
    [storePageEvent],
  );

  return (
    <div className={styles.card}>
      <div className={styles.content}>
        <div className={iconTile}>
          <FanProtectFillIcon size={24} />
        </div>
        <div className={styles.textBlock}>
          <Heading variant="title" size="small" as="h2">
            {isStubhub ? (
              /* "{0} Guarantee" */
              <FormatContent id={contentIds.guaranteeTitle} params={[FAN_PROTECT_TRADEMARK]} />
            ) : (
              /* "viagogo Guarantee" */
              <Content id={VIAGOGO_GUARANTEE_CONTENT_ID} />
            )}
          </Heading>
          <Text variant="body" size="small" color="text.subtle">
            {/* "Your order is 100% guaranteed" */}
            <Content id={contentIds.guaranteeDesc} />
          </Text>
        </div>
        <ChevronRightIcon classes={{ root: styles.chevronIcon ?? "" }} size={20} />
      </div>
      <div className={styles.chipsRow}>
        {chips.map(({ icon, contentId, value }) => (
          <Tag
            key={contentId}
            onClick={() => handleChipClick(value)}
            startIcon={icon}
            kind={LegacyTagKind.Info}
            variant={LegacyTagVariant.Borderless}
            size={LegacyTagSize.Medium}
          >
            {/* "On time" | "Valid" | "Protected" */}
            <Content id={contentId} />
          </Tag>
        ))}
      </div>
    </div>
  );
};
